/**
 * PaginatedTable - Reusable paginated table component
 * Automatically calculates page size based on available space
 */

import type { Component, ReactNode } from 'react';

// React is available globally from CDN
const React = (window as any).React;
const ReactComponent = React.Component;

interface PaginatedTableProps {
    items: any[];
    renderRow: (item: any, index: number) => ReactNode;
    headers: ReactNode;
    colgroup?: ReactNode; // Optional colgroup for column widths
    minItemsPerPage?: number;
    maxHeight?: string;
    className?: string;
    rowHeight?: number; // Row height in pixels for calculation
    currentPage?: number;
    onPageChange?: (page: number) => void;
}

interface PaginatedTableState {
    currentPage: number;
    itemsPerPage: number;
}

class PaginatedTable extends ReactComponent<PaginatedTableProps, PaginatedTableState> {
    private containerRef: HTMLDivElement | null = null;
    private headerHeight: number = 0;
    private rowHeight: number; // Row height in pixels for calculation
    private paginationHeight: number = 60; // Space for pagination controls

    constructor(props: PaginatedTableProps) {
        super(props);
        this.rowHeight = props.rowHeight || 60; // Default to 60px
        this.state = {
            currentPage: 1,
            itemsPerPage: props.minItemsPerPage || 10
        };
    }

    componentDidMount(): void {
        // Use setTimeout to ensure the container is fully rendered
        setTimeout(() => {
            this.calculateItemsPerPage();
        }, 0);
        window.addEventListener('resize', this.handleResize);
    }

    componentWillUnmount(): void {
        window.removeEventListener('resize', this.handleResize);
    }

    componentDidUpdate(prevProps: PaginatedTableProps): void {
        if (prevProps.items.length !== this.props.items.length || 
            prevProps.rowHeight !== this.props.rowHeight) {
            this.calculateItemsPerPage();
        }

        const totalPages = this.getTotalPages();
        const currentPage = this.getCurrentPage();
        if (totalPages > 0 && currentPage > totalPages) {
            this.updateCurrentPage(totalPages);
        }
    }

    handleResize = (): void => {
        this.calculateItemsPerPage();
    };

    calculateItemsPerPage(): void {
        if (!this.containerRef) return;

        // Always use current rowHeight from props
        const currentRowHeight = this.props.rowHeight || 60;
        this.rowHeight = currentRowHeight;

        const container = this.containerRef;
        
        // Get the table container div (the scrollable one)
        const tableContainer = container.querySelector('div[style*="overflow"]') as HTMLElement;
        if (!tableContainer) return;
        
        // Get the table element to measure header height
        const table = tableContainer.querySelector('table');
        const thead = table?.querySelector('thead');
        const headerHeight = thead?.clientHeight || currentRowHeight;
        
        // Get pagination controls height - use getBoundingClientRect for accurate measurement
        const paginationEl = container.querySelector('.pagination-controls') as HTMLElement;
        const actualPaginationHeight = paginationEl ? Math.ceil(paginationEl.getBoundingClientRect().height) : this.paginationHeight;
        
        // Available height = table container height (not full container)
        // The table container is: container height - pagination height
        const tableContainerHeight = tableContainer.clientHeight;
        const availableHeight = tableContainerHeight - headerHeight;
        
        // Calculate items per page - must fit exactly, no overflow
        // Calculate what actually fits first
        const itemsThatFit = Math.floor(availableHeight / currentRowHeight);
        // Only use minItemsPerPage if it actually fits, otherwise use what fits
        const calculatedItems = itemsThatFit >= (this.props.minItemsPerPage || 5) 
            ? itemsThatFit 
            : Math.min(itemsThatFit, this.props.minItemsPerPage || 5);

        // DEBUG: Log calculation values
        // console.log('PaginatedTable calculation:', {
        //     containerHeight: container.clientHeight,
        //     tableContainerHeight: tableContainerHeight,
        //     paginationHeight: actualPaginationHeight,
        //     headerHeight: headerHeight,
        //     availableHeight: availableHeight,
        //     rowHeight: currentRowHeight,
        //     calculatedItems: calculatedItems,
        //     totalTableHeight: headerHeight + (calculatedItems * currentRowHeight),
        //     currentItemsPerPage: this.state.itemsPerPage
        // });

        if (calculatedItems !== this.state.itemsPerPage) {
            this.setState({ itemsPerPage: calculatedItems });
        }
    }

    getTotalPages(): number {
        return Math.ceil(this.props.items.length / this.state.itemsPerPage);
    }

    getCurrentPage(): number {
        if (typeof this.props.currentPage === 'number') {
            return this.props.currentPage;
        }
        return this.state.currentPage;
    }

    getCurrentPageItems(): any[] {
        const currentPage = this.getCurrentPage();
        const start = (currentPage - 1) * this.state.itemsPerPage;
        const end = start + this.state.itemsPerPage;
        return this.props.items.slice(start, end);
    }

    updateCurrentPage(page: number): void {
        if (typeof this.props.currentPage === 'number') {
            if (this.props.onPageChange) {
                this.props.onPageChange(page);
            }
            return;
        }
        this.setState({ currentPage: page });
    }

    handlePrevious = (): void => {
        const currentPage = this.getCurrentPage();
        if (currentPage > 1) {
            this.updateCurrentPage(currentPage - 1);
        }
    };

    handleNext = (): void => {
        const currentPage = this.getCurrentPage();
        if (currentPage < this.getTotalPages()) {
            this.updateCurrentPage(currentPage + 1);
        }
    };

    handlePageClick = (page: number): void => {
        this.updateCurrentPage(page);
    };

    render(): ReactNode {
        const totalPages = this.getTotalPages();
        const currentItems = this.getCurrentPageItems();
        const currentPage = this.getCurrentPage();
        const startItem = (currentPage - 1) * this.state.itemsPerPage + 1;
        const endItem = Math.min(startItem + this.state.itemsPerPage - 1, this.props.items.length);

        // Generate pagination structure - always 7 elements: [1] [ellipsis1] [mid1] [mid2] [mid3] [ellipsis2] [last]
        // Use visibility: hidden for ellipses when not needed to maintain fixed width
        const getPaginationStructure = (): Array<{ type: 'page' | 'ellipsis', value: number, visible: boolean }> => {
            const total = totalPages;
            const current = currentPage;
            const structure: Array<{ type: 'page' | 'ellipsis', value: number, visible: boolean }> = [];

            if (total <= 7) {
                // Show all pages if 7 or fewer
                for (let i = 1; i <= total; i++) {
                    structure.push({ type: 'page', value: i, visible: true });
                }
            } else {
                // Always show page 1
                structure.push({ type: 'page', value: 1, visible: true });
                
                // Ellipsis 1 - visible if current page is far from start
                const showEllipsis1 = current > 4;
                structure.push({ type: 'ellipsis', value: -1, visible: showEllipsis1 });
                
                // Calculate 3 middle pages
                let mid1: number, mid2: number, mid3: number;
                
                if (current <= 4) {
                    // Near start: show 2, 3, 4
                    mid1 = 2;
                    mid2 = 3;
                    mid3 = 4;
                } else if (current >= total - 3) {
                    // Near end: show total-3, total-2, total-1
                    mid1 = total - 3;
                    mid2 = total - 2;
                    mid3 = total - 1;
                } else {
                    // In middle: show current-1, current, current+1
                    mid1 = current - 1;
                    mid2 = current;
                    mid3 = current + 1;
                }
                
                structure.push({ type: 'page', value: mid1, visible: true });
                structure.push({ type: 'page', value: mid2, visible: true });
                structure.push({ type: 'page', value: mid3, visible: true });
                
                // Ellipsis 2 - visible if current page is far from end
                const showEllipsis2 = current < total - 3;
                structure.push({ type: 'ellipsis', value: -2, visible: showEllipsis2 });
                
                // Always show last page
                structure.push({ type: 'page', value: total, visible: true });
            }

            return structure;
        };

        return React.createElement('div', {
            className: `paginated-table ${this.props.className || ''}`,
            ref: (el: HTMLDivElement) => { this.containerRef = el; },
            style: {
                display: 'flex',
                flexDirection: 'column',
                height: this.props.maxHeight || '100%',
                minHeight: '300px',
                overflow: 'hidden'
            }
        },
            // Table container with scroll
            React.createElement('div', {
                style: {
                    flex: '1 1 auto',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    minHeight: 0
                }
            },
                React.createElement('table', {
                    style: {
                        width: '100%',
                        borderCollapse: 'collapse',
                        tableLayout: 'fixed'
                    }
                },
                    this.props.colgroup || null,
                    this.props.headers,
                    React.createElement('tbody', null,
                        currentItems.map((item, index) => {
                            const rowElement = this.props.renderRow(item, startItem - 1 + index);
                            // renderRow should return a tr element - we need to clone it and add height
                            if (rowElement && typeof rowElement === 'object' && (rowElement as any).type === 'tr') {
                                const originalProps = (rowElement as any).props || {};
                                return React.createElement('tr', {
                                    key: originalProps.key || `item-${startItem - 1 + index}`,
                                    style: { 
                                        height: `${this.rowHeight}px`,
                                        ...(originalProps.style || {})
                                    },
                                    ...Object.keys(originalProps).filter(k => k !== 'style' && k !== 'key').reduce((acc, k) => {
                                        acc[k] = originalProps[k];
                                        return acc;
                                    }, {} as any)
                                }, originalProps.children);
                            }
                            // Fallback: wrap in tr if renderRow doesn't return a tr
                            return React.createElement('tr', { 
                                key: `item-${startItem - 1 + index}`,
                                style: { height: `${this.rowHeight}px` }
                            }, rowElement);
                        }),
                        // Add empty rows if needed to fill the page
                        currentItems.length < this.state.itemsPerPage && Array.from({ length: this.state.itemsPerPage - currentItems.length }).map((_, idx) =>
                            React.createElement('tr', { 
                                key: `empty-${idx}`,
                                className: 'empty-row',
                                style: { height: `${this.rowHeight}px` }
                            },
                                React.createElement('td', { colSpan: 100 })
                            )
                        )
                    )
                )
            ),
            // Pagination controls (always show, even if only one page)
            React.createElement('div', {
                className: 'pagination-controls',
                style: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                    flex: '0 0 auto'
                }
            },
                React.createElement('div', {
                    style: {
                        fontSize: '14px',
                        color: 'var(--main-text-color)',
                        opacity: 0.8
                    }
                }, `Showing ${startItem}-${endItem} of ${this.props.items.length}`),
                React.createElement('div', {
                    style: {
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center'
                    }
                },
                    React.createElement('button', {
                        className: 'btn',
                        onClick: this.handlePrevious,
                        disabled: currentPage === 1,
                        style: { padding: '4px 12px', fontSize: '14px' }
                    }, '← Previous'),
                    getPaginationStructure().map((item, idx) => {
                        if (item.type === 'ellipsis') {
                            return React.createElement('span', {
                                key: `ellipsis-${idx}`,
                                style: {
                                    padding: '4px 12px',
                                    fontSize: '14px',
                                    minWidth: '36px',
                                    textAlign: 'center',
                                    visibility: item.visible ? 'visible' : 'hidden'
                                }
                            }, '...');
                        } else {
                            const isActive = item.value === currentPage;
                            return React.createElement('button', {
                                key: `page-${item.value}`,
                                className: `btn ${isActive ? 'active' : ''}`,
                                onClick: () => this.handlePageClick(item.value),
                                style: {
                                    padding: '4px 12px',
                                    fontSize: '14px',
                                    minWidth: '36px',
                                    fontWeight: isActive ? 'bold' : 'normal',
                                    backgroundColor: isActive ? 'rgba(0, 0, 0, 0.2)' : 'transparent',
                                    border: isActive ? '2px solid var(--main-text-color)' : '1px solid rgba(0, 0, 0, 0.2)',
                                    color: isActive ? 'var(--main-text-color)' : 'inherit'
                                }
                            }, String(item.value));
                        }
                    }),
                    React.createElement('button', {
                        className: 'btn',
                        onClick: this.handleNext,
                        disabled: currentPage === totalPages,
                        style: { padding: '4px 12px', fontSize: '14px' }
                    }, 'Next →')
                )
            )
        );
    }
}

(window as any).PaginatedTable = PaginatedTable;
export default PaginatedTable;
